// A sidebar entry with children (Resources → People / Assets / …), collapsed
// until wanted — Zanetto Tools' NavGroup (client/src/components/Layout.jsx),
// ported. The group auto-expands while the route is inside the section; the
// chevron can override that until the next navigation, when the route takes
// back over — so a manually-collapsed group doesn't stay stuck closed after
// moving elsewhere and back.
//
// One deliberate departure from ZT: there the whole row is the toggle and the
// section root is not a sidebar destination at all. Dayboard's section roots
// are real pages (the Resources home is the doors into the groups), so the row
// stays a link and only the chevron toggles.
import { useEffect, useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import type { NavItem } from './navConfig';

interface NavGroupProps {
  /** The parent item, with `children` already filtered to the viewer's role. */
  item: NavItem & { children: NonNullable<NavItem['children']> };
  linkClass: (state: { isActive: boolean; section?: boolean }) => string;
  childLinkClass: (state: { isActive: boolean }) => string;
}

export function NavGroup({ item, linkClass, childLinkClass }: NavGroupProps) {
  const location = useLocation();
  const inSection =
    location.pathname === item.to || location.pathname.startsWith(`${item.to}/`);
  const [manual, setManual] = useState<boolean | null>(null);
  const open = manual ?? inSection;
  useEffect(() => {
    setManual(null);
  }, [location.pathname]);

  // Exactly on the section root the row wears the active brand fill, so the
  // chevron has to swap to ink with it.
  const atRoot = location.pathname === item.to;

  return (
    <div>
      <div className="relative">
        <NavLink
          to={item.to}
          end
          className={({ isActive }) =>
            `${linkClass({ isActive, section: inSection && !open })} !pr-8`
          }
        >
          {item.icon}
          {item.label}
        </NavLink>
        <button
          type="button"
          onClick={() => setManual(!open)}
          aria-expanded={open}
          aria-label={`${item.label} group`}
          className={`absolute right-1 top-1/2 -translate-y-1/2 rounded p-1.5 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-400 ${
            atRoot ? 'text-ink' : 'text-slate-400 hover:text-white'
          }`}
        >
          <svg
            className={`h-4 w-4 transition-transform ${open ? 'rotate-90' : ''}`}
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={2}
            aria-hidden="true"
          >
            <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
          </svg>
        </button>
      </div>
      {open && (
        <div className="mt-0.5 space-y-0.5">
          {item.children.map((child) => (
            // `end` on a child whose path is a PREFIX of its siblings' — the
            // Forms group's "Complete a form" is /app/forms itself, and
            // without it the row stays lit while you are on Submissions or
            // Form templates (round 37).
            <NavLink key={child.to} to={child.to} end={child.end} className={childLinkClass}>
              {child.label}
            </NavLink>
          ))}
        </div>
      )}
    </div>
  );
}
